import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../services/authService';

interface UseUserStatusCheckOptions {
  checkInterval?: number; // milliseconds, default 10000
  onDeactivated?: () => void;
  onApprovalRevoked?: () => void;
}

/**
 * Hook to poll for user status changes (deactivation, approval revocation)
 * Automatically logs out or redirects user if status changes
 */
export const useUserStatusCheck = (options: UseUserStatusCheckOptions = {}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { checkInterval = 10000, onDeactivated, onApprovalRevoked } = options;

  useEffect(() => {
    if (!user?.uid) return;

    const checkUserStatus = async () => {
      try {
        const userData = await AuthService.getUserData(user.uid);
        if (userData) {
          // If user was deactivated, log them out immediately
          if (userData.isActive === false) {
            console.log('useUserStatusCheck: User has been deactivated! Logging out');
            onDeactivated?.();
            await logout();
            navigate('/login', { replace: true });
            return;
          }
          // If user approval was revoked, redirect to pending approval
          if (!userData.isApproved) {
            console.log('useUserStatusCheck: Approval was revoked! Redirecting');
            onApprovalRevoked?.();
            navigate('/pending-approval', { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('useUserStatusCheck error:', error);
      }
    };

    // Check every interval
    const interval = setInterval(checkUserStatus, checkInterval);

    return () => clearInterval(interval);
  }, [user?.uid, navigate, logout, checkInterval, onDeactivated, onApprovalRevoked]);
};
